// Auto-recover from stale / purged build assets after a deploy.
//
// The messenger runs inside an iframe served from messenger-app.gleap.io. On
// every redeploy the content-hashed bundle filenames rotate and the previous
// build's files are purged. An iframe loaded BEFORE the deploy still references
// the old hashes; because the host falls back to index.html (HTTP 200,
// text/html) for any missing path, the request for the old chunk/stylesheet does
// not 404 — it returns HTML, so the stylesheet is dropped (icons balloon, see
// #136545) or a script fails to execute. The inline :where() rule in index.html
// keeps icons sane meanwhile; this guard fully recovers by reloading once to
// pull the fresh document, which references the CURRENT hashes.
//
// A 15s sessionStorage guard prevents reload loops on a genuinely broken build.
// (Most effective once missing assets return a real 404 — see public/_redirects;
// a 404'd <link>/<script> reliably fires the `error` event handled below.)

const RELOAD_GUARD_KEY = "gleap_chunk_reload_at";
// Don't reload again within this window — if a chunk error recurs this fast it is
// not a transient stale-deploy, so we stop to avoid an infinite reload loop.
const RELOAD_DEBOUNCE_MS = 15000;

const recentlyReloaded = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || "0");
    return last > 0 && Date.now() - last < RELOAD_DEBOUNCE_MS;
  } catch {
    // sessionStorage unavailable (private mode / partitioned iframe storage).
    return false;
  }
};

const reloadOnce = (reason: string): void => {
  if (recentlyReloaded()) {
    console.error(
      `[chunkReloadGuard] chunk error persists after a recent reload (${reason}); not reloading again.`
    );
    return;
  }
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // If we cannot persist the guard we must not reload, or we risk a loop.
    console.error(
      `[chunkReloadGuard] stale chunk detected (${reason}) but the reload guard is unavailable; skipping auto-reload.`
    );
    return;
  }
  console.warn(
    `[chunkReloadGuard] stale chunk detected (${reason}); reloading to fetch the latest build.`
  );
  window.location.reload();
};

const looksLikeChunkError = (message?: string): boolean => {
  if (!message) {
    return false;
  }
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) || // Safari
    /Refused to execute script/i.test(message) ||
    /MIME type \('text\/html'\)/i.test(message)
  );
};

// Only react to OUR own hashed bundles, never third-party scripts/styles.
const isOurStaticAsset = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  try {
    const parsed = new URL(url, window.location.href);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith("/static/")
    );
  } catch {
    return false;
  }
};

export const installChunkReloadGuard = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  if ((window as any).__gleapChunkReloadGuardInstalled) {
    return;
  }
  (window as any).__gleapChunkReloadGuardInstalled = true;

  // Failed <script>/<link> element loads. These fire on the element and do NOT
  // bubble, so listen in the capture phase. Covers a purged JS chunk and a
  // dropped stylesheet once missing assets return a real 404 (_redirects).
  window.addEventListener(
    "error",
    (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (
        !target ||
        (target.tagName !== "SCRIPT" && target.tagName !== "LINK")
      ) {
        return;
      }
      const url =
        (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;
      if (isOurStaticAsset(url)) {
        reloadOnce(`asset-load:${target.tagName.toLowerCase()}`);
      }
    },
    true
  );

  // ChunkLoadError surfaced as an unhandled promise rejection (webpack runtime).
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason: any = event?.reason;
      const message =
        typeof reason === "string" ? reason : reason?.message || reason?.name;
      if (looksLikeChunkError(message)) {
        reloadOnce("unhandledrejection");
      }
    }
  );
};
